/**
 * Core gamification (P0): EXP/gold curves for `ProgressAction` completions + the shared
 * `TaskDifficulty` / `TaskPriority` multipliers used across personal Apply grants and
 * campaign capacity math. Curves are intentionally simple/linear-with-floor — the plan
 * (`docs/plans/core-gamification.plan.md`) does not lock exact numbers, only that
 * `completionExp = f(estimatedMinutes) × difficultyMultiplier`.
 */

export type TaskDifficulty = "TRIVIAL" | "EASY" | "MEDIUM" | "HARD" | "EXTREME";
export type TaskPriority = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";

/** Base player EXP per estimated minute before difficulty scaling. */
export const PROGRESS_ACTION_EXP_PER_MINUTE = 2;
/** Floor so trivially short actions still grant something. */
export const PROGRESS_ACTION_MIN_COMPLETION_EXP = 2;

/** Base gold per estimated minute — gold is scarcer than EXP in this economy. */
export const PROGRESS_ACTION_GOLD_PER_MINUTE = 0.5;
export const PROGRESS_ACTION_MIN_COMPLETION_GOLD = 1;

export const DIFFICULTY_MULTIPLIERS: Record<TaskDifficulty, number> = {
	TRIVIAL: 0.5,
	EASY: 0.75,
	MEDIUM: 1,
	HARD: 1.5,
	EXTREME: 2,
};

export const PRIORITY_MULTIPLIERS: Record<TaskPriority, number> = {
	CRITICAL: 1.5,
	HIGH: 1.25,
	MEDIUM: 1,
	LOW: 0.75,
};

function clampMinutes(minutes: number): number {
	if (!Number.isFinite(minutes)) {
		return 0;
	}
	return Math.max(0, Math.floor(minutes));
}

/** `f(estimatedMinutes)` — base player EXP before any difficulty multiplier. */
export function computeProgressActionCompletionExp(estimatedMinutes: number): number {
	const minutes = clampMinutes(estimatedMinutes);
	if (minutes <= 0) {
		return 0;
	}
	return Math.max(
		PROGRESS_ACTION_MIN_COMPLETION_EXP,
		Math.round(minutes * PROGRESS_ACTION_EXP_PER_MINUTE),
	);
}

/** Gold companion curve — only granted by uncapped packages (module ACTION / campaign), never personal pool. */
export function computeProgressActionGold(estimatedMinutes: number): number {
	const minutes = clampMinutes(estimatedMinutes);
	if (minutes <= 0) {
		return 0;
	}
	return Math.max(
		PROGRESS_ACTION_MIN_COMPLETION_GOLD,
		Math.round(minutes * PROGRESS_ACTION_GOLD_PER_MINUTE),
	);
}

/** Multiplier applied to base EXP for personal Task/Habit assignment difficulty. */
export function difficultyMultiplier(difficulty: TaskDifficulty): number {
	return DIFFICULTY_MULTIPLIERS[difficulty] ?? 1;
}

/** Multiplier applied to campaign component weight / contribution credit for priority. */
export function priorityMultiplier(priority: TaskPriority): number {
	return PRIORITY_MULTIPLIERS[priority] ?? 1;
}

export interface ComputeTaskHabitCompletionExpParams {
	estimatedMinutes: number;
	difficulty: TaskDifficulty;
}

/**
 * `completionExp = f(estimatedMinutes) × difficultyMultiplier` (personal daily pool section,
 * D33). Callers apply `grant = min(computed, remaining)` against `UserDailyActionExpPool`.
 */
export function computeTaskHabitCompletionExp(
	params: ComputeTaskHabitCompletionExpParams,
): number {
	const base = computeProgressActionCompletionExp(params.estimatedMinutes);
	if (base <= 0) {
		return 0;
	}
	return Math.max(1, Math.round(base * difficultyMultiplier(params.difficulty)));
}

export interface ClampPersonalDailyPoolGrantParams {
	computedExp: number;
	grantedExp: number;
	capExp: number;
}

export interface ClampPersonalDailyPoolGrantResult {
	grant: number;
	remainingAfter: number;
}

/**
 * Shared daily cap for personal tasks+habits (D33): `grant = min(computed, remaining)`.
 * No carry-over — `remainingAfter` reflects the cap state immediately after this grant.
 */
export function clampPersonalDailyPoolGrant(
	params: ClampPersonalDailyPoolGrantParams,
): ClampPersonalDailyPoolGrantResult {
	const remainingBefore = Math.max(0, params.capExp - params.grantedExp);
	const grant = Math.min(Math.max(0, params.computedExp), remainingBefore);
	return {
		grant,
		remainingAfter: Math.max(0, remainingBefore - grant),
	};
}
